import { useMemo } from 'react';

import * as THREE from 'three';

import { Segmentation } from '@core';

import { getKeypointId, getSafeSkeleton } from '../helpers';
import { SWING_PLANE_KEYS, SWING_PLANES_LIST } from '../mappings';
import { FrameFirstClubTrack, FrameFirstSkeleton } from '../skeletonUtils';
import { averagePosition } from '../utils/averagePosition';
import { projectPointOntoPlane } from '../utils/getPointInPlane';
import { PlanePosition } from '../visualizations/SwingPlane';

import type { Data3D, KeypointIdToName } from '../types';

const getShaftLineAtPosition = (
    positionKey: keyof Segmentation,
    clubTrack: FrameFirstClubTrack,
    segmentation: Segmentation,
) => {
    const frameIndex = segmentation[positionKey];
    if (typeof frameIndex === 'undefined') return null;
    const start = clubTrack['shaft_start_coordinates']?.[frameIndex];
    const end = clubTrack['shaft_end_coordinates']?.[frameIndex];
    if (!start || !end) return null;
    return {
        start: new THREE.Vector3(...start),
        end: new THREE.Vector3(...end),
    };
};

const getShaftPlaneCenterAndNormal = ({ start, end }: Record<'start' | 'end', THREE.Vector3>) => {
    const shaftDirection = new THREE.Vector3().subVectors(end, start).normalize();

    const up = new THREE.Vector3(0, 1, 0);
    const planeNormal = new THREE.Vector3().crossVectors(shaftDirection, up).normalize();

    const center = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
    const direction = new THREE.Vector3().subVectors(start, end).normalize();
    const offset = direction.multiplyScalar(0.5);

    return { center: new THREE.Vector3().copy(center).add(offset), normal: planeNormal };
};

// Get line from ball (origin) to a keypoint at a given position
const getKeypointLineAtPosition = (
    positionKey: keyof Segmentation,
    keypointName: string,
    skeleton: FrameFirstSkeleton,
    keypointIdToName: KeypointIdToName,
    segmentation: Segmentation,
    golfBallTranslation: [number, number, number] | undefined,
) => {
    const frameIndex = segmentation[positionKey];
    if (typeof frameIndex === 'undefined') return null;

    const keypointId = getKeypointId(keypointName, keypointIdToName);
    const coords = getSafeSkeleton(skeleton, frameIndex, keypointId);
    if (!coords) return null;

    // Ball is at origin, keypoint is at coords (same coordinate system as club track)
    return {
        start: new THREE.Vector3(...(golfBallTranslation ?? [0, 0, 0])),
        end: new THREE.Vector3(...coords),
    };
};

export const usePlanePositions = ({
    swingPlane,
    clubTrack,
    segmentation,
    skeleton,
    keypointIdToName,
    golfBallTranslation,
}: Pick<
    Data3D,
    'swingPlane' | 'clubTrack' | 'segmentation' | 'skeleton' | 'keypointIdToName' | 'golfBallTranslation'
>) => {
    const swingPlanePosition = useMemo(() => {
        if (!swingPlane) return null;
        const center = new THREE.Vector3(swingPlane[3], swingPlane[4], swingPlane[5]);
        return {
            normal: new THREE.Vector3(swingPlane[0], swingPlane[1], swingPlane[2]),
            center: center,
        };
    }, [swingPlane]);

    const setupPlanePosition = useMemo(() => {
        const shaftPositions = getShaftLineAtPosition('P1', clubTrack, segmentation);
        if (!shaftPositions) {
            return null;
        }
        return getShaftPlaneCenterAndNormal(shaftPositions);
    }, [clubTrack, segmentation]);

    const impactPlanePosition = useMemo(() => {
        const shaftPositions = getShaftLineAtPosition('P7', clubTrack, segmentation);
        if (!shaftPositions) {
            return null;
        }
        return getShaftPlaneCenterAndNormal(shaftPositions);
    }, [clubTrack, segmentation]);

    const trailElbowPlanePosition = useMemo(() => {
        if (!skeleton || !keypointIdToName || !segmentation) return null;
        const elbowLine = getKeypointLineAtPosition(
            'P1',
            'right_elbow',
            skeleton,
            keypointIdToName,
            segmentation,
            golfBallTranslation,
        );
        if (!elbowLine) return null;
        return getShaftPlaneCenterAndNormal(elbowLine);
    }, [skeleton, keypointIdToName, segmentation, golfBallTranslation]);

    const trailShoulderPlanePosition = useMemo(() => {
        if (!skeleton || !keypointIdToName || !segmentation) return null;
        const shoulderLine = getKeypointLineAtPosition(
            'P1',
            'right_shoulder',
            skeleton,
            keypointIdToName,
            segmentation,
            golfBallTranslation,
        );
        if (!shoulderLine) return null;
        return getShaftPlaneCenterAndNormal(shoulderLine);
    }, [skeleton, keypointIdToName, segmentation, golfBallTranslation]);

    const averageCenter = useMemo(() => {
        return averagePosition(
            [
                swingPlanePosition?.center,
                setupPlanePosition?.center,
                impactPlanePosition?.center,
                trailElbowPlanePosition?.center,
                trailShoulderPlanePosition?.center,
            ].filter((c) => !!c),
        );
    }, [
        impactPlanePosition?.center,
        setupPlanePosition?.center,
        swingPlanePosition?.center,
        trailElbowPlanePosition?.center,
        trailShoulderPlanePosition?.center,
    ]);

    const swingPlanes = useMemo(() => {
        //Map swing planes into a list and prepare key/val object for use
        const planesList = SWING_PLANES_LIST.map<(PlanePosition & { type: string }) | null>((planeType) => {
            if (!averageCenter) {
                return null;
            }
            if (planeType === SWING_PLANE_KEYS.SWING_PLANE && swingPlanePosition) {
                const { normal, center } = swingPlanePosition;
                return {
                    normal,
                    center: projectPointOntoPlane(averageCenter, center, normal),
                    type: planeType,
                };
            }
            if (planeType === SWING_PLANE_KEYS.SETUP_PLANE && setupPlanePosition) {
                const { normal, center } = setupPlanePosition;
                return {
                    normal,
                    center: projectPointOntoPlane(averageCenter, center, normal),
                    type: planeType,
                };
            }
            if (planeType === SWING_PLANE_KEYS.IMPACT_PLANE && impactPlanePosition) {
                const { normal, center } = impactPlanePosition;
                return {
                    normal,
                    center: projectPointOntoPlane(averageCenter, center, normal),
                    type: planeType,
                };
            }
            if (planeType === SWING_PLANE_KEYS.TRAIL_ELBOW_PLANE && trailElbowPlanePosition) {
                const { normal, center } = trailElbowPlanePosition;
                return {
                    normal,
                    center: projectPointOntoPlane(averageCenter, center, normal),
                    type: planeType,
                };
            }
            if (planeType === SWING_PLANE_KEYS.TRAIL_SHOULDER_PLANE && trailShoulderPlanePosition) {
                const { normal, center } = trailShoulderPlanePosition;
                return {
                    normal,
                    center: projectPointOntoPlane(averageCenter, center, normal),
                    type: planeType,
                };
            }
            return null;
        }).filter((pos) => !!pos);

        return Object.fromEntries(planesList.map((plane) => [plane.type, plane]));
    }, [
        averageCenter,
        impactPlanePosition,
        setupPlanePosition,
        swingPlanePosition,
        trailElbowPlanePosition,
        trailShoulderPlanePosition,
    ]);

    return swingPlanes;
};
